import { FrontendCondition, observedFrontendCondition } from '../../../shared/semantic/index';
import type { CapabilityDefinitionReadPort } from '../definition/adapters/capability-definition-port';
import {
  capabilityDefinitionQuery,
  CapabilityDefinitionSnapshotViolation,
  type CapabilityDefinitionSnapshotV1,
} from '../definition/consumer/contract';
import { readCapabilityDefinition } from '../definition/consumer/reader';
import { projectCapabilityDefinition } from '../definition/domain/projection';
import type { CapabilityDefinitionDomainState } from '../definition/domain/state';
import { rememberDefinition } from '../domain/collection';

export const CENTER_DATA_MODE = 'REAL_LOCAL_SYNTHETIC_HTTP';
export type ReadFailure = 'TRANSPORT_ERROR' | 'WIRE_INVALID' | 'REQUEST_REJECTED' | null;
export interface CenterReadState {
  readonly requestId: number;
  readonly submittedId: string | null;
  readonly result: CapabilityDefinitionDomainState | null;
  readonly failure: ReadFailure;
  readonly collection: readonly CapabilityDefinitionSnapshotV1[];
}
export function initialCenterReadState(): CenterReadState {
  return { requestId: 0, submittedId: null, result: null, failure: null, collection: [] };
}

// The existing parser exposes Error (no typed wire error). Match only its current
// finite public validation messages; never display the caught diagnostic.
const wireMessages = new Set([
  'Invalid capability definition object',
  'Invalid capability definition fields',
  'Invalid capability definition text',
  'Invalid capability definition version',
  'Invalid capability definition lifecycle',
  'Capability identity mismatch',
  'Unexpected capability definition snapshot',
  'Invalid capability definition status',
  'Capability definition JSON required',
]);
function failureKind(error: unknown): ReadFailure {
  return error instanceof SyntaxError ||
    error instanceof CapabilityDefinitionSnapshotViolation ||
    (error instanceof Error && wireMessages.has(error.message))
    ? 'WIRE_INVALID'
    : 'TRANSPORT_ERROR';
}

/** Request identity belongs to this page; disposal invalidates without changing the frozen Port. */
export function createCenterReadSession(
  port: CapabilityDefinitionReadPort,
  changed: (state: CenterReadState) => void,
) {
  let state = initialCenterReadState();
  let sequence = 0;
  let disposed = false;
  function publish(next: CenterReadState) {
    state = next;
    if (!disposed) changed(state);
  }
  return {
    async submit(id: string): Promise<void> {
      if (disposed) return;
      const requestId = ++sequence;
      const base = { ...state, requestId, submittedId: id, failure: null };
      if (!id.trim() || id === '.' || id === '..') {
        publish({ ...base, result: null, failure: 'REQUEST_REJECTED' });
        return;
      }
      publish({
        ...base,
        result: projectCapabilityDefinition(observedFrontendCondition(FrontendCondition.LOADING)),
      });
      let failure: ReadFailure = null;
      const observedPort: CapabilityDefinitionReadPort = {
        async read(query) {
          try {
            return await port.read(query);
          } catch (error) {
            failure = failureKind(error);
            throw error;
          }
        },
      };
      try {
        const observation = await readCapabilityDefinition(
          observedPort,
          capabilityDefinitionQuery(id),
        );
        if (disposed || sequence !== requestId) return;
        const result = projectCapabilityDefinition(observation);
        publish({
          ...base,
          result,
          failure,
          collection:
            result.kind === 'DEFINITION_READABLE'
              ? rememberDefinition(state.collection, result.snapshot)
              : state.collection,
        });
      } catch {
        if (!disposed && sequence === requestId) {
          publish({
            ...base,
            collection: state.collection,
            failure: 'REQUEST_REJECTED',
            result: projectCapabilityDefinition(observedFrontendCondition(FrontendCondition.ERROR)),
          });
        }
      }
    },
    cancel() {
      if (disposed) return;
      sequence += 1;
      publish({ ...state, requestId: sequence, submittedId: null, result: null, failure: null });
    },
    dispose() {
      disposed = true;
      sequence += 1;
    },
    current: () => state,
  };
}
